import { EnvironmentService } from 'src/app/services/environment.service';
import { ReviewSectionStore } from './review-section.store';
import { Component, Input } from '@angular/core';
import {
  SectionIdentifier,
  SectionMessage,
  SectionReview,
  SectionStatus,
  ShowButtonsPredicateContext,
  SkExecutorAction,
} from './review-section.domain';
import { ReviewSectionService } from './review-section.service';
import { map } from 'rxjs/operators';
import { UserService } from 'src/app/services/user.service';
import { ReviewSectionSelectors } from './review-section.selectors';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { OrderProcesStore } from '../../stores/order-process.store';

const defaultActiveExecutorTypes: SkExecutorAction[] = [
  SkExecutorAction.accept_application,
  SkExecutorAction.accept_offer,
  SkExecutorAction.create_offer,
  SkExecutorAction.request_update_application,
  SkExecutorAction.update_application,
  SkExecutorAction.approve_offer,
];

const chimneySweeperActiveExecutorTypes: SkExecutorAction[] = [
  SkExecutorAction.cs_accept_application,
  SkExecutorAction.cs_reject_application,
];

export const hideButtonsForChimneySweeper = (context: ShowButtonsPredicateContext) => {
  return context.orderProcess.chimney_sweeper_id && context.orderProcess.chimney_sweeper_id !== '0'
    ? context.userRole !== 'chimney_sweeper'
    : true;
};

export const hideButtonsForEmployeeWhenHasChimneySweeper = (context: ShowButtonsPredicateContext) => {
  return context.orderProcess.chimney_sweeper_id && context.orderProcess.chimney_sweeper_id !== '0'
    ? context.userRole !== 'employee'
    : true;
};

@Component({
  selector: 'app-review-section',
  templateUrl: './review-section.component.html',
  styleUrls: ['./review-section.component.scss'],
})
export class ReviewSectionComponent extends DestroyableComponent implements DestroyableComponent {
  review: SectionReview;
  areButtonsDisabled = false;
  isInternalUser: boolean;
  areButtonsVisible: boolean = true;

  showUnlockTooltip = true;
  showLockTooltip = true;
  showComments = true;
  sectionMessages: SectionMessage[];

  showRequestChangeBtn = this.environmentService.showRequestChangeBtn;
  _applicationId: number;
  canCreateMessages: boolean;
  applicationHasChimneySweeper: boolean;

  constructor(
    private reviewSectionService: ReviewSectionService,
    private reviewSectionStore: ReviewSectionStore,
    private orderProcessStore: OrderProcesStore,
    private userService: UserService,
    private reviewSectionSelectors: ReviewSectionSelectors,
    private environmentService: EnvironmentService
  ) {
    super();
  }

  public isDataEditable: boolean = true;

  @Input() title: string;
  @Input() tooltip: string;
  @Input() showTooltip: boolean = true;
  @Input() tooltipStyleClass: string = 'netz-info-tooltip';
  @Input() tooltipPosition: 'left' | 'bottom' = 'left';
  @Input() sectionId: SectionIdentifier;
  @Input() set applicationId(value: number | string) {
    if (!value) return;
    this._applicationId = parseInt(value.toString());
  }
  @Input() isDetailsView: boolean;
  @Input() showAcceptRejectButtons: boolean = true;

  /// If the user has any of the roles in the excludeRoles array, the buttons will be disabled
  @Input() showButtonsPredicate: (context: ShowButtonsPredicateContext) => boolean;

  sectionStatus = {
    approved: SectionStatus.approved,
    rejected: SectionStatus.rejected,
  };

  ngOnInit(): void {
    this.areButtonsDisabled = !this.userService.isUserModeratorRole();

    this.subscribeToSectionMessages();

    this.subscribeToSectionWaitingForUpdateCheck();

    this.checkButtonsActivity(() => {
      this.addSectionReviewToStoreIfNeeded();
    });
  }

  private checkButtonsActivity(onSuccess?: any) {
    this.preventLeak(this.orderProcessStore.getOrderProcessValue()).subscribe(orderProcess => {
      if (!orderProcess) return;

      const activeExecutorTypes = this.userService.isChimneySweeper() ? chimneySweeperActiveExecutorTypes : defaultActiveExecutorTypes;
      const isLocked = !activeExecutorTypes.map(x => x.toString()).includes(orderProcess.sk_executor_action);

      if (this.userService.isChimneySweeper()) {
        this.areButtonsDisabled = isLocked;
      } else {
        this.areButtonsDisabled = isLocked || !this.userService.isUserModeratorRole();
      }

      this.canCreateMessages = !isLocked;

      if (this.showButtonsPredicate) {
        this.areButtonsVisible = this.showButtonsPredicate({ component: this, orderProcess, userRole: this.userService.getUserRole() });
      }

      if (onSuccess) {
        onSuccess();
      }
    });
  }

  private addSectionReviewToStoreIfNeeded() {
    this.preventLeak(this.reviewSectionSelectors.getSectionReview(this._applicationId, this.sectionId)).subscribe(async review => {
      if (!review && this.showAcceptRejectButtons && this.areButtonsVisible) {
        /* adding a review section for state tracking purposes if the review for
         this section is not yet available*/
        this.reviewSectionStore.updateReview({
          application_id: this._applicationId,
          id: 0,
          reviewed_on: new Date(),
          reviewer_id: 0,
          section: this.sectionId,
          status: SectionStatus.none,
        });
      }

      this.review = review;
    });
  }

  private subscribeToSectionMessages() {
    this.preventLeak(
      this.reviewSectionStore.sectionMessages.pipe(
        map(messages =>
          messages.filter(message => {
            return message.section === this.sectionId;
          })
        ),
        map(messages =>
          messages.map(m => {
            m.content = m.content.replace(/\n/g, '<br/>'); // Fix the new lines
            return m;
          })
        )
      )
    ).subscribe(messages => (this.sectionMessages = messages));
  }

  private subscribeToSectionWaitingForUpdateCheck() {
    this.preventLeak(this.reviewSectionSelectors.isSectionWaitingForUpdate(this._applicationId, this.sectionId)).subscribe(
      isWaitingForUpdate => {
        if (this.isDetailsView) {
          this.isDataEditable = this.showRequestChangeBtn && this.userService.isUserTechnicianRole() && isWaitingForUpdate;
        } else {
          this.isDataEditable = true;
        }
      }
    );
  }

  createSectionReview(status: SectionStatus) {
    if (this.areButtonsDisabled) return;

    this.preventLeak(
      this.reviewSectionService.createSectionReview({ application_id: this._applicationId, section: this.sectionId, status: status })
    ).subscribe(review => {
      this.reviewSectionStore.updateReview(review);
    });
  }
}
